import type { MouseEvent } from 'react'
import { Clock, ExternalLink, Plus, RefreshCw } from 'react-feather'

import { IconGlitter } from '@avantstay/backoffice-vectors'

import { RemySession } from './remySessions'
import * as P from './AskAiChatSidePanel.styles'
import * as S from './SessionViews.styles'

// Compact "x ago" for the session list, falling back to a date past a week.
const formatRelativeTime = (iso: string): string => {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const minutes = Math.round((Date.now() - then) / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 7) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

// Shown when Remy reports the user has not connected the Voyage connector yet
// (first visit, or a disconnected account). Chat turns would be rejected, so
// the panel offers the one-time connect flow before anything else. The flow
// opens in a popup the panel watches; the plain link is the popup-blocked
// fallback, and the manual recheck covers both.
export const OnboardingGate = ({
  connectUrl,
  checking,
  onConnect,
  onConnected,
}: {
  connectUrl: string
  checking: boolean
  onConnect: (event: MouseEvent<HTMLAnchorElement>) => void
  onConnected: () => void
}) => (
  <P.Centered>
    <P.IconBadge>
      <IconGlitter width={28} height={28} />
    </P.IconBadge>
    <P.PanelTitle>Finish setting up Remy</P.PanelTitle>
    <P.PanelText>
      Remy needs to be connected to your Voyage account before you can chat. It's a one-time step
      and takes under a minute.
    </P.PanelText>
    <S.GateActions>
      <S.PrimaryButton
        as="a"
        href={connectUrl}
        target="_blank"
        rel="noreferrer"
        onClick={onConnect}
      >
        <ExternalLink size={16} />
        Connect to Remy
      </S.PrimaryButton>
      <S.SecondaryButton type="button" disabled={checking} onClick={onConnected}>
        <RefreshCw size={16} />
        {checking ? 'Checking…' : "I've connected — continue"}
      </S.SecondaryButton>
    </S.GateActions>
  </P.Centered>
)

// Initial "continue or start fresh" prompt, shown only when the user has prior
// web sessions to resume.
export const SessionGate = ({
  sessions,
  onContinue,
  onNew,
}: {
  sessions: RemySession[]
  onContinue: () => void
  onNew: () => void
}) => (
  <P.Centered>
    <P.IconBadge>
      <IconGlitter width={28} height={28} />
    </P.IconBadge>
    <P.PanelTitle>Welcome back</P.PanelTitle>
    <P.PanelText>
      You have {sessions.length} recent conversation{sessions.length === 1 ? '' : 's'} with Remy.
      Pick up where you left off, or start fresh?
    </P.PanelText>
    <S.GateActions>
      <S.PrimaryButton type="button" onClick={onContinue}>
        <Clock size={16} />
        Continue a previous chat
      </S.PrimaryButton>
      <S.SecondaryButton type="button" onClick={onNew}>
        <Plus size={16} />
        Start new chat
      </S.SecondaryButton>
    </S.GateActions>
  </P.Centered>
)

// List of resumable sessions; selecting one loads its history into the chat.
export const SessionPicker = ({
  sessions,
  resumingId,
  error,
  onPick,
  onNew,
}: {
  sessions: RemySession[]
  resumingId: string | null
  error: string | null
  onPick: (externalId: string) => void
  onNew: () => void
}) => (
  <S.Picker>
    <S.PickerTitle>Continue a conversation</S.PickerTitle>
    {error && <S.ErrorBanner role="alert">{error}</S.ErrorBanner>}
    <S.SessionList>
      {sessions.map(session => (
        <S.SessionItem
          key={session.externalId}
          type="button"
          disabled={resumingId !== null}
          onClick={() => onPick(session.externalId)}
        >
          <S.SessionTitle>{session.title?.trim() || 'Untitled conversation'}</S.SessionTitle>
          <S.SessionMeta>
            {session.turnCount} message{session.turnCount === 1 ? '' : 's'} ·{' '}
            {formatRelativeTime(session.lastActivityAt)}
            {resumingId === session.externalId ? ' · Loading…' : ''}
          </S.SessionMeta>
        </S.SessionItem>
      ))}
    </S.SessionList>
    <S.SecondaryButton type="button" onClick={onNew} disabled={resumingId !== null}>
      <Plus size={16} />
      Start new chat
    </S.SecondaryButton>
  </S.Picker>
)
